import { t, type StringKey } from '../strings';

/**
 * A call from a record placed through the office number (piece 21, *Communications seam*; piece 35, *Calls through
 * the office number*; decision 5130). The provider rings the rep's own phone first and then the contact, who sees the
 * company number. These are the plan's own words for the call's state, the routes the phone and the website reach,
 * and the note a call leaves, so the server and both screens read one shape.
 */

/** 🔒 The plan's own nine, Missed and Voicemail an inbound call's (decision 11780); a vendor's status never leaves the adapter. */
export const CALL_STATUSES = ['Placing', 'Ringing', 'InProgress', 'Completed', 'NoAnswer', 'Busy', 'Failed', 'Missed', 'Voicemail'] as const;
export type CallStatus = (typeof CALL_STATUSES)[number];

/** The six a call ends on. */
export const CALL_ENDINGS: readonly CallStatus[] = ['Completed', 'NoAnswer', 'Busy', 'Failed', 'Missed', 'Voicemail'];

export function isCallEnded(status: CallStatus): boolean {
  return CALL_ENDINGS.includes(status);
}

/** 🔨 How far a status stands; a callback never moves a row to a lower one (decision 5130). */
export function callStatusRank(status: CallStatus): number {
  return isCallEnded(status) ? 3 : status === 'InProgress' ? 2 : status === 'Ringing' ? 1 : 0;
}

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const CALLS_BRIDGE_PATH = '/calls/bridge';

/** One call's row, which the Calling page polls. */
export function callPath(callId: string): string {
  return `/calls/${encodeURIComponent(callId)}`;
}

/** The record a call is placed from, and the line pressed where the phone's tile named one. */
export interface BridgeCallBody {
  recordType: 'PersonRecord' | 'CompanyRecord';
  recordId: string;
  contactMethodId?: string | null;
}

/** Whether the office call is offered to this viewer, and why not where it is not. */
export interface BridgeOffer {
  offered: boolean;
  reason: 'noVoice' | 'noMobile' | null;
}

/** What `calls/bridge` and `calls/:callId` answer: one row as the Calling page reads it. */
export interface BridgedCallView {
  id: string;
  status: CallStatus;
  recordType: string;
  recordId: string;
  contactName: string;
  /** Whether a person answered the rep's leg; a call nobody answered there called nobody else. */
  repAnswered: boolean;
  durationSeconds: number | null;
  noteId: string | null;
  /** Whether the call has nothing left to write: its note is on the record, or it will write none. */
  settled: boolean;
}

/** 🔒 Every poll of a call's row is two seconds apart, and there is no socket (decision 5130). */
export const CALL_POLL_MS = 2_000;

/** 🔨 The fields a poll answer carries, surviving one missing a key, as every screen reading a server row does. */
export function bridgedCallOf(body: unknown): BridgedCallView | null {
  const row = (body ?? {}) as Partial<BridgedCallView>;
  if (typeof row.id !== 'string' || !CALL_STATUSES.includes(row.status as CallStatus)) return null;
  return {
    id: row.id,
    status: row.status as CallStatus,
    recordType: typeof row.recordType === 'string' ? row.recordType : '',
    recordId: typeof row.recordId === 'string' ? row.recordId : '',
    contactName: typeof row.contactName === 'string' ? row.contactName : '',
    repAnswered: row.repAnswered === true,
    durationSeconds: typeof row.durationSeconds === 'number' ? row.durationSeconds : null,
    noteId: typeof row.noteId === 'string' ? row.noteId : null,
    settled: row.settled === true,
  };
}

/** A status's own key segment: the plan's member with its first letter lowered, as every string key is written. */
function keyOf(status: CallStatus): string {
  return status.charAt(0).toLowerCase() + status.slice(1);
}

/** The Calling page's line for where the call stands. */
export function callStateLine(view: Pick<BridgedCallView, 'status' | 'repAnswered'>): string {
  // A call the provider refused never rang anybody; one whose rep never answered called nobody else.
  if (isCallEnded(view.status) && view.status !== 'Failed' && !view.repAnswered) return t('calls.state.repMissed');
  return t(`calls.state.${keyOf(view.status)}` as StringKey);
}

/** The line under it once the call has ended: the note written, being written, or none. */
export function callNoteLine(view: Pick<BridgedCallView, 'status' | 'repAnswered' | 'noteId' | 'settled'>): string | null {
  if (!isCallEnded(view.status) || !view.repAnswered) return null;
  if (view.noteId) return t('calls.note.written');
  return view.settled ? null : t('calls.note.writing');
}

/** Whether the page stops asking: the call is over and its note is written, or it writes none. */
export function callPollDone(view: Pick<BridgedCallView, 'status' | 'settled'>): boolean {
  return isCallEnded(view.status) && view.settled;
}

/** The note's length: whole minutes, rounded up, and none for a call that never connected. */
export function callNoteMinutes(durationSeconds: number | null): number | null {
  if (durationSeconds === null || durationSeconds <= 0) return null;
  return Math.ceil(durationSeconds / 60);
}

/**
 * 🔒 The Log Contact note a call leaves (decision 5130): the recording's link as its first line, then the transcript
 * under a Transcript heading. A call that never connected says how it ended, and one with no recording says it was
 * placed through the office number. `late` marks a connected call written without all it waited for (decision 5300).
 */
export function callNoteText(input: { status: CallStatus; recordingUrl: string | null; transcript: string | null; late?: boolean }): string {
  const lines: string[] = [];
  if (input.recordingUrl) lines.push(`${t('calls.note.recording')} ${input.recordingUrl}`);
  else lines.push(t(`calls.note.outcome.${keyOf(isCallEnded(input.status) ? input.status : 'Completed')}` as StringKey));
  const transcript = input.transcript?.trim() ?? '';
  // 🔒 Decision 5300: a connected call written without what never came says so directly after the outcome line, rather than reading as a call with none.
  if (input.late) {
    if (!input.recordingUrl) lines.push(t('calls.note.recordingMissing'));
    if (transcript === '') lines.push(t('calls.note.transcriptMissing'));
  }
  if (transcript !== '') lines.push('', t('calls.note.transcript'), transcript);
  return lines.join('\n');
}

/**
 * 🔒 The Log Contact note an inbound call leaves on its matched record (decision 11781): the recording's link first
 * where a message was left, else how the call ended, then the transcript under its heading. `late` says what never came.
 */
export function inboundCallNoteText(input: { status: CallStatus; recordingUrl: string | null; transcript: string | null; late?: boolean }): string {
  const lines: string[] = [];
  if (input.recordingUrl) lines.push(`${t('calls.note.recording')} ${input.recordingUrl}`);
  lines.push(t(input.status === 'Voicemail' ? 'calls.note.outcome.voicemail' : input.status === 'Missed' ? 'calls.note.outcome.missed' : 'calls.note.outcome.answered'));
  const transcript = input.transcript?.trim() ?? '';
  if (input.late && input.status === 'Voicemail' && transcript === '') lines.push(t('calls.note.transcriptMissing'));
  if (transcript !== '') lines.push('', t('calls.note.transcript'), transcript);
  return lines.join('\n');
}
